import type * as Y from 'yjs'
import { boundingRect, type BoardElement } from '@praesto/shared'
import { addElement, createId, nextZ } from './elements.js'

/**
 * Interne Zwischenablage.
 *
 * Bewusst nicht die des Systems: Ein Board-Element ist kein Text und kein Bild,
 * das andere Programme verstünden, und der Zugriff auf die Systemablage
 * verlangte zusätzliche Berechtigungen. Innerhalb von Præsto — auch zwischen
 * zwei Boards im selben Browser — reicht dieser Speicher.
 */
let clipboard: BoardElement[] = []

export const copyElements = (elements: readonly BoardElement[]): void => {
  clipboard = elements.map((element) => ({ ...element }))
}

export const hasClipboard = (): boolean => clipboard.length > 0

/** Versatz beim Einfügen, damit die Kopie nicht exakt aufs Original fällt. */
const OFFSET = 32

/**
 * Einfügen — mehrere Elemente behalten dabei ihre Anordnung zueinander.
 *
 * Verschoben wird die ganze Gruppe als eine: Wer drei Kacheln nebeneinander
 * kopiert, will drei Kacheln nebeneinander wiederhaben und nicht dreimal
 * dasselbe an derselben Stelle.
 */
export function pasteElements(doc: Y.Doc, at?: { x: number; y: number }): string[] {
  if (clipboard.length === 0) return []
  const bounds = boundingRect(clipboard)
  if (!bounds) return []

  const dx = at ? at.x - bounds.w / 2 - bounds.x : OFFSET
  const dy = at ? at.y - bounds.h / 2 - bounds.y : OFFSET

  const ids: string[] = []
  doc.transact(() => {
    for (const element of clipboard) {
      const id = createId()
      ids.push(id)
      addElement(doc, { ...element, id, x: element.x + dx, y: element.y + dy, z: nextZ(doc) })
    }
  })
  return ids
}

export function duplicateElements(doc: Y.Doc, elements: readonly BoardElement[]): string[] {
  const ids: string[] = []
  doc.transact(() => {
    for (const element of elements) {
      const id = createId()
      ids.push(id)
      addElement(doc, { ...element, id, x: element.x + OFFSET, y: element.y + OFFSET, z: nextZ(doc) })
    }
  })
  return ids
}
